import { getEncounterTimelineData } from 'app/frontend/base/services/raidPlanData'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import {
  getAssignmentScopeIndexes,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
} from '../../TeamPlans/components/assignmentTimeline'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import {
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
} from './eventActivityTimelineResolver'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'

type TimedExportLine = {
  at: number
  line: string
}

type AbilitySummary = {
  label: string
  slotOrder: string[]
  tokensBySlot: Map<string, string[]>
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const normalizeRealmChatName = (realmName: string) =>
  realmName.replaceAll(' ', '').replaceAll('-', '')

const unavailableStatuses: wowaudit.EventParticipantReference['status'][] = [
  'absent',
  'missing',
  'removed',
]

const formatTimeValue = (value: number) =>
  Math.max(Math.floor(value), 0).toString()

const normalizeRoleToken = (role: string) => {
  if (role === 'heal') return 'healer'
  return role
}

const toEntityToken = (entity: ResolvedAssignmentEntity) => {
  if (entity.kind === 'participant') return entity.participant.name
  if (entity.sourceEntity.kind === 'everyone') return 'everyone'
  if (entity.sourceEntity.kind === 'group')
    return `group${entity.sourceEntity.group}`
  if (entity.sourceEntity.kind === 'role') {
    return normalizeRoleToken(entity.sourceEntity.role)
  }
  if (entity.sourceEntity.kind === 'collection') {
    if (entity.sourceEntity.collection === 'everyone') return 'everyone'
    if (entity.sourceEntity.collection === 'healers') return 'healer'
    if (entity.sourceEntity.collection === 'tanks') return 'tank'
    return entity.sourceEntity.collection
  }
  if (entity.sourceEntity.kind === 'by_role') {
    return normalizeRoleToken(entity.sourceEntity.role)
  }
  if (entity.sourceEntity.kind === 'member') return entity.sourceEntity.label
  return ''
}

const computePhaseBoundaries = (
  maxTime: number,
  defaultPhaseBoundaries: Array<number | undefined>,
  persistedPhaseBoundaries: unknown,
) => {
  const persisted = Array.isArray(persistedPhaseBoundaries)
    ? persistedPhaseBoundaries
    : []
  return defaultPhaseBoundaries.reduce<number[]>(
    (currentBoundaries, defaultBoundary, index) => {
      const prevCurrentBoundary =
        index === 0 ? 0 : currentBoundaries[index - 1] ?? 0
      const prevDefaultBoundary =
        index === 0 ? 0 : defaultPhaseBoundaries[index - 1] ?? 0
      const shiftBeforePhase = prevCurrentBoundary - prevDefaultBoundary
      const defaultBoundaryWithShift =
        (defaultBoundary ?? maxTime) + shiftBeforePhase
      const persistedBoundary = persisted[index]
      const rawBoundary =
        typeof persistedBoundary === 'number'
          ? persistedBoundary
          : defaultBoundaryWithShift
      const maxBoundary = Math.max(defaultBoundaryWithShift, rawBoundary)
      currentBoundaries.push(
        clamp(rawBoundary, prevCurrentBoundary, maxBoundary),
      )
      return currentBoundaries
    },
    [],
  )
}

const getPhaseAt = (time: number, phaseBoundaries: number[]) => {
  for (
    let phaseIndex = 0;
    phaseIndex < phaseBoundaries.length;
    phaseIndex += 1
  ) {
    if (time < phaseBoundaries[phaseIndex]) {
      return phaseIndex
    }
  }
  return Math.max(phaseBoundaries.length - 1, 0)
}

const getPhaseStart = (phaseIndex: number, phaseBoundaries: number[]) => {
  if (phaseIndex <= 0) return 0
  return phaseBoundaries[phaseIndex - 1] ?? 0
}

const getBossTimelineEntries = ({
  maxTime,
  phaseBoundaries,
  phases,
}: {
  maxTime: number
  phaseBoundaries: number[]
  phases: Array<{
    abilities: Array<{
      spell_id: number
      entries: Array<{ time: number; duration: number }>
    }>
  }>
}) => {
  const defaultPhaseBoundaries = phases.map((phase) => {
    return phase.abilities
      .flatMap((ability) =>
        ability.entries.map((entry) => entry.time + entry.duration),
      )
      .reduce<number | undefined>(
        (currentMax, endAt) =>
          currentMax === undefined ? endAt : Math.max(currentMax, endAt),
        undefined,
      )
  })
  return phases.flatMap((phase, phaseIndex) => {
    const phaseStart =
      phaseIndex === 0 ? 0 : phaseBoundaries[phaseIndex - 1] ?? 0
    const phaseEnd = phaseBoundaries[phaseIndex] ?? maxTime
    const shiftBeforePhase =
      phaseIndex === 0
        ? 0
        : (phaseBoundaries[phaseIndex - 1] ?? 0) -
          (defaultPhaseBoundaries[phaseIndex - 1] ?? 0)
    return phase.abilities.flatMap((ability) =>
      ability.entries
        .map((entry) => Number((entry.time + shiftBeforePhase).toFixed(2)))
        .filter((entryTime) => entryTime >= phaseStart && entryTime < phaseEnd)
        .map((entryTime) => ({
          spellId: ability.spell_id,
          time: entryTime,
        })),
    )
  })
}

const getNearestBossSpellId = (
  castAt: number,
  bossEntries: Array<{ spellId: number; time: number }>,
) => {
  const candidates = bossEntries
    .map((entry) => ({
      spellId: entry.spellId,
      delta: Math.abs(entry.time - castAt),
      time: entry.time,
    }))
    .filter((entry) => entry.delta <= 3)
    .sort((left, right) => {
      if (left.delta !== right.delta) return left.delta - right.delta
      if (left.time !== right.time) return left.time - right.time
      return left.spellId - right.spellId
    })
  return candidates[0]?.spellId
}

const toAssignmentEntityTokens = (entities: ResolvedAssignmentEntity[]) =>
  entities
    .filter((entity) => !entity.issue)
    .map(toEntityToken)
    .filter((token) => token.length > 0)

const appendTextProperty = (parts: string[], text?: string) => {
  const trimmed = text?.trim()
  if (trimmed) {
    parts.push(`text:${trimmed}`)
  }
}

const buildInviteList = ({
  activity,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants?: ResolvedAssignmentParticipant[]
}) => {
  const selectedParticipantIds = new Set(
    (selectedParticipants ?? []).map(
      (participant) => participant.participantId,
    ),
  )
  const fallbackEntries = allEntries.filter((entry) => {
    if (unavailableStatuses.includes(entry.participant.status)) return false
    return entry.selections[activity.id]?.selected === true
  })
  const baseEntries =
    selectedParticipantIds.size > 0
      ? allEntries.filter((entry) =>
          selectedParticipantIds.has(entry.participant.id),
        )
      : fallbackEntries

  return baseEntries
    .map((entry) => resolveEventParticipantCharacter(entry, activity.id))
    .map((character) => ({
      id: character.id,
      name: character.name,
      value: `${character.name}-${normalizeRealmChatName(
        character.realm.name,
      )}`,
    }))
    .reduce<Array<{ id: number; name: string; value: string }>>((acc, item) => {
      if (acc.some((entry) => entry.id === item.id)) return acc
      acc.push(item)
      return acc
    }, [])
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((item) => item.value)
}

export const buildEventActivityInviteListLine = ({
  activity,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants?: ResolvedAssignmentParticipant[]
}) => {
  const inviteList = buildInviteList({
    activity,
    allEntries,
    selectedParticipants,
  })
  return `invitelist:${inviteList.join(' ')};`
}

export const buildEventActivityLinkedPlanNote = ({
  activity,
  linkedPlanData,
  allEntries,
}: {
  activity: wowaudit.EventActivity
  linkedPlanData: EventActivityLinkedPlanData
  allEntries: EventActivityParticipantEntry[]
}) => {
  const linkedRaidPlan = linkedPlanData.linkedRaidPlan
  const encounter = activity.encounter
  if (!linkedRaidPlan || !encounter) return ''
  const encounterTimeline =
    getEncounterTimelineData()[linkedRaidPlan.encounterId.toString()]
  if (!encounterTimeline) return ''
  const encounterDifficulty =
    encounterTimeline.difficulties.find(
      (difficulty) => difficulty.difficulty === linkedRaidPlan.difficulty,
    ) ?? encounterTimeline.difficulties[0]
  if (!encounterDifficulty) return ''
  const roundUpToThirtySeconds = (value: number) => Math.ceil(value / 30) * 30
  const encounterMax = encounterDifficulty.maxTime ?? 0
  const maxTime =
    encounterMax > 0
      ? roundUpToThirtySeconds(Math.max(60, encounterMax))
      : roundUpToThirtySeconds(
          Math.max(
            60,
            encounterDifficulty.phases
              .flatMap((phase) => phase.abilities)
              .flatMap((ability) =>
                ability.entries.map((entry) => entry.time + entry.duration),
              )
              .reduce((max, endAt) => Math.max(max, endAt), 0) ?? 0,
            linkedRaidPlan.timeline.actors.reduce((max, actor) => {
              return actor.casts.reduce((innerMax, cast) => {
                if (cast.spell_id === null) return innerMax
                return Math.max(innerMax, cast.at)
              }, max)
            }, 0),
          ),
        )
  const defaultPhaseBoundaries = encounterDifficulty.phases.map((phase) => {
    return phase.abilities
      .flatMap((ability) =>
        ability.entries.map((entry) => entry.time + entry.duration),
      )
      .reduce<number | undefined>(
        (currentMax, endAt) =>
          currentMax === undefined ? endAt : Math.max(currentMax, endAt),
        undefined,
      )
  })
  const phaseBoundaries = computePhaseBoundaries(
    maxTime,
    defaultPhaseBoundaries,
    linkedRaidPlan.timeline.phase_boundaries ??
      linkedRaidPlan.timeline?.phase_boundaries,
  )
  const bossEntries = getBossTimelineEntries({
    maxTime,
    phaseBoundaries,
    phases: encounterDifficulty.phases,
  })

  const castLines: TimedExportLine[] = linkedPlanData.resolvedActors
    .filter(
      (resolvedActor) => !resolvedActor.issue && resolvedActor.participant,
    )
    .flatMap((resolvedActor) =>
      resolvedActor.actor.casts
        .filter((cast) => cast.spell_id !== null)
        .map((cast) => {
          const phaseIndex = getPhaseAt(cast.at, phaseBoundaries)
          const phaseStart = getPhaseStart(phaseIndex, phaseBoundaries)
          const relativeTime = Math.max(cast.at - phaseStart, 0)
          const parts = [
            `time:${formatTimeValue(relativeTime)}`,
            `ph:${phaseIndex + 1}`,
          ]
          const bossSpellId = getNearestBossSpellId(cast.at, bossEntries)
          if (bossSpellId) {
            parts.push(`bossSpell:${bossSpellId}`)
          }
          parts.push(
            `tag:${
              resolvedActor.participant?.name ?? resolvedActor.actor.label
            }`,
          )
          parts.push(`spellid:${cast.spell_id}`)
          appendTextProperty(parts, cast.note)
          return {
            at: cast.at,
            line: `${parts.join(';')};`,
          }
        }),
    )
    .sort((left, right) => {
      if (left.at !== right.at) return left.at - right.at
      return left.line.localeCompare(right.line)
    })

  const rowsBySpellId = linkedPlanData.selectedRows.reduce<
    Record<number, typeof linkedPlanData.selectedRows[number]>
  >((acc, row) => {
    acc[row.spellId] = row
    return acc
  }, {})
  const assignmentLines: TimedExportLine[] = []
  const summaryByAbility = new Map<string, AbilitySummary>()

  linkedPlanData.linkedPlanAssignments.forEach((ability) => {
    const row = rowsBySpellId[ability.spell_id]
    if (!row) return
    const assignmentGroups = getEffectiveAssignmentGroups(
      row.assignmentGroups,
      ability.assignment_groups,
    )
    const offsetSeconds =
      typeof ability.offset_seconds === 'number' &&
      Number.isFinite(ability.offset_seconds)
        ? ability.offset_seconds
        : 0
    const assignmentLabel = ability.message?.trim().length
      ? ability.message.trim()
      : `Soak ${row.name}`
    const viewMode = getEffectiveAssignmentViewMode(ability.view_mode)
    const customGrouping = getEffectiveAbilityCustomGrouping(
      ability.spell_id,
      ability.custom_grouping,
    )
    const scopes = getAssignmentScopeIndexes(
      row.entries,
      viewMode,
      customGrouping,
    )
    scopes.forEach((scope) => {
      for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
        const key = `${row.spellId}:${scope.id}:${boxIndex}`
        const resolvedEntities =
          linkedPlanData.resolverResult.resolvedByScopeBoxKey[key] ?? []
        const tokens = toAssignmentEntityTokens(resolvedEntities)
        if (!tokens.length) continue
        scope.castGroups.forEach((castGroup) => {
          const firstCastIndex = castGroup[0]
          const firstEntry = row.entries[firstCastIndex]
          if (!firstEntry) return
          const castAt = firstEntry.time + offsetSeconds
          const phaseIndex = getPhaseAt(castAt, phaseBoundaries)
          const phaseStart = getPhaseStart(phaseIndex, phaseBoundaries)
          const relativeTime = Math.max(castAt - phaseStart, 0)
          const parts = [
            `time:${formatTimeValue(relativeTime)}`,
            `ph:${phaseIndex + 1}`,
            `tag:${tokens.join(' ')}`,
            `text:${assignmentLabel}`,
          ]
          assignmentLines.push({
            at: castAt,
            line: `${parts.join(';')};`,
          })
        })

        const abilitySummary = summaryByAbility.get(row.name) ?? {
          label: assignmentLabel,
          slotOrder: [],
          tokensBySlot: new Map<string, string[]>(),
        }
        const summarySlotKey = `${scope.id}:${boxIndex}`
        if (!abilitySummary.tokensBySlot.has(summarySlotKey)) {
          abilitySummary.slotOrder.push(summarySlotKey)
        }
        const existingTokens =
          abilitySummary.tokensBySlot.get(summarySlotKey) ?? []
        tokens.forEach((token) => {
          if (!existingTokens.includes(token)) {
            existingTokens.push(token)
          }
        })
        abilitySummary.tokensBySlot.set(summarySlotKey, existingTokens)
        summaryByAbility.set(row.name, abilitySummary)
      }
    })
  })

  assignmentLines.sort((left, right) => {
    if (left.at !== right.at) return left.at - right.at
    return left.line.localeCompare(right.line)
  })

  const summaryLines = Array.from(summaryByAbility.entries()).flatMap(
    ([, abilitySummary], abilityIndex) => {
      const lines = [
        abilitySummary.label,
        ...abilitySummary.slotOrder.map((slotKey, slotIndex) => {
          const tokens = abilitySummary.tokensBySlot.get(slotKey) ?? []
          return `Soak ${slotIndex + 1}: ${tokens.join(', ')}`
        }),
      ]
      if (abilityIndex < summaryByAbility.size - 1) {
        lines.push('')
      }
      return lines
    },
  )

  const difficultyLabel =
    difficultiesLabels[activity.difficulty] ?? activity.difficulty
  const header = `EncounterID:${encounter.wclId};Difficulty:${difficultyLabel};Name:${encounter.name}`
  const timedLines = [
    ...castLines.map((entry) => entry.line),
    ...assignmentLines.map((entry) => entry.line),
  ]
  const chunks = [
    header,
    '',
    ...timedLines,
    '',
    ...summaryLines,
    '',
    buildEventActivityInviteListLine({
      activity,
      allEntries,
      selectedParticipants: linkedPlanData.selectedParticipants,
    }),
  ]
  return chunks.join('\n')
}
